import React, { useState } from 'react';

/**
 * SnapshotBadge — shows the AI pipeline that produced a piece of content.
 * Compact by default; expands on click.
 *
 * snapshot shape:
 *  { summary, stage1_models, autoresearch, autoresearch_confidence,
 *    judge, feedback_memories_applied, cta_url,
 *    mode, rounds, council_size, models_per_round }
 */
export default function SnapshotBadge({ snapshot, type = 'article' }) {
  const [open, setOpen] = useState(false);
  if (!snapshot) return null;

  const summary = snapshot.summary || 'AI pipeline';
  const memories = snapshot.feedback_memories_applied || 0;

  return (
    <div
      className="rounded-lg text-xs cursor-pointer select-none"
      style={{
        background: 'rgba(61,255,160,0.06)',
        border: '1px solid rgba(61,255,160,0.18)',
        padding: open ? '10px 14px' : '5px 12px',
        transition: 'all 0.2s',
      }}
      onClick={() => setOpen(o => !o)}
      title="Click to expand pipeline details"
    >
      {/* Collapsed: single-line summary */}
      <div className="flex items-center gap-2 flex-wrap">
        <span style={{ color: '#3dffa0', fontWeight: 600, fontSize: 10, letterSpacing: '0.05em' }}>
          PIPELINE
        </span>
        <span style={{ color: 'rgba(255,255,255,0.55)' }} className="truncate max-w-xs">
          {summary}
        </span>
        {memories > 0 && (
          <span
            style={{
              background: 'rgba(124,94,250,0.2)',
              color: '#a68eff',
              borderRadius: 4,
              padding: '1px 6px',
            }}
          >
            {memories} lesson{memories !== 1 ? 's' : ''} applied
          </span>
        )}
        <span style={{ color: 'rgba(255,255,255,0.3)', marginLeft: 'auto' }}>
          {open ? '▲' : '▼'}
        </span>
      </div>

      {/* Expanded: full breakdown */}
      {open && (
        <div className="mt-3 space-y-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
          {type === 'article' && (
            <>
              {snapshot.stage1_models?.length > 0 && (
                <Row label="Stage 1 — Drafts" value={snapshot.stage1_models.join(', ')} ok />
              )}
              {snapshot.autoresearch !== undefined && (
                <Row
                  label="Stage 2 — Autoresearch"
                  value={
                    snapshot.autoresearch
                      ? `Yes${snapshot.autoresearch_confidence ? ` (${(snapshot.autoresearch_confidence * 100).toFixed(0)}% confidence)` : ''}`
                      : 'Skipped'
                  }
                  ok={snapshot.autoresearch}
                  dim={!snapshot.autoresearch}
                />
              )}
              {snapshot.judge && (
                <Row label="Stage 3 — Judge" value={snapshot.judge} ok />
              )}
              {snapshot.cta_url && (
                <Row label="CTA URL" value={snapshot.cta_url} ok />
              )}
            </>
          )}

          {type === 'brainstorm' && (
            <>
              <Row label="Mode" value={snapshot.mode || '—'} ok />
              <Row label="Rounds" value={String(snapshot.rounds || 0)} ok />
              <Row label="Council size" value={`${snapshot.council_size || '?'} models/round`} ok />
              {snapshot.judge && <Row label="Judge" value={snapshot.judge} ok />}
              {snapshot.models_per_round?.length > 0 && (
                <div className="mt-1">
                  {snapshot.models_per_round.map((models, i) => (
                    <Row
                      key={i}
                      label={`Round ${i + 1}`}
                      value={Array.isArray(models) ? models.join(', ') : String(models)}
                      ok
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {memories > 0 && (
            <Row
              label="Self-learning"
              value={`${memories} feedback lesson${memories !== 1 ? 's' : ''} from memory applied`}
              ok
              accent
            />
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, ok, dim, accent }) {
  return (
    <div className="flex gap-2 items-start">
      <span
        style={{
          color: accent ? '#a68eff' : ok ? '#3dffa0' : dim ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.45)',
          minWidth: 8,
          marginTop: 1,
        }}
      >
        {ok ? '✓' : '–'}
      </span>
      <span style={{ color: 'rgba(255,255,255,0.4)', minWidth: 120 }}>{label}:</span>
      <span
        style={{
          color: accent ? '#a68eff' : 'rgba(255,255,255,0.7)',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </div>
  );
}
